import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext, OrgContext, SetupTrackerContext } from '../../../objects/Context';
import CircleInitials from '../../../components/CircleInitials/CircleInitials';
import { ReactComponent as DownIcon } from '../../../assets/icons/down-icon.svg';
import Popup from '../../../components/Popup/Popup';
import LoadingScreen from '../../../components/LoadingScreen/LoadingScreen';
import InviteModule from './HelperComponents/InviteModule';
import WorkspaceAccessDropdown from './HelperComponents/WorkspaceAccessDropdown';
import './SettingsComponents.css';
import './OrgMembersSettings.css';

function OrgMembersSettings() {
    const { user } = useContext(UserContext);
    const { org } = useContext(OrgContext);
    const { setupSteps, setSetupSteps } = useContext(SetupTrackerContext);
    const isCurrentUserAdmin = user?.orgRole === 'admin';
    const [members, setMembers] = useState([]);
    const [invitesSent, setInvitesSent] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectionOpen, setSelectionOpen] = useState(null);
    const [inviteModuleOpen, setInviteModuleOpen] = useState(false);
    // New state for role update dropdown
    const [updateRoleOpen, setUpdateRoleOpen] = useState(null);
    // New state for workspace access dropdown
    const [workspaceAccessOpen, setWorkspaceAccessOpen] = useState(null);
    // Loading states
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [loadingInvites, setLoadingInvites] = useState(false);
    // Remove user confirmation
    const [removeTarget, setRemoveTarget] = useState(null);
    const [removingUser, setRemovingUser] = useState(false);

    // Combine members and invites for display
    const allMembers = [
        ...members.map(member => ({ 
            ...member, 
            isInvite: false,
            // For existing members, we'll need to fetch their workspace access
            // For now, we'll use a placeholder - in a real implementation, this would come from the API
            workspaceIds: member.workspaceIds || [],
            accessLevel: member.accessLevel || 'full'
        })),
        ...invitesSent.map(invite => ({ 
            id: invite.id || `invite-${invite.email}`,
            name: invite.email.split('@')[0], // Use email prefix as name
            email: invite.email,
            orgRole: invite.orgRole || 'member',
            accessLevel: invite.accessLevel || 'full',
            workspaceIds: invite.workspaceIds || [],
            isInvite: true
        }))
    ];

    /////////////////// USE EFFECTS ///////////////////
    useEffect(() => {
        if (org.id) {
            fetchMembers();
            fetchInvitesSent();
        }
    }, [org.id]);



    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.members-table')) {
                setSelectionOpen(null);
                setUpdateRoleOpen(null);
                setWorkspaceAccessOpen(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    /////////////////// DATABASE FUNCTIONS ///////////////////
    
    const fetchMembers = async () => {
        setLoadingMembers(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/orgs/get-members/${org.id}`, { withCredentials: true });
            setMembers(Array.isArray(response.data) ? response.data : [response.data]);
        } catch (error) {
            console.error('Error fetching members:', error);
        } finally {
            setLoadingMembers(false);
        }
    };

    const fetchInvitesSent = async () => {
        setLoadingInvites(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/orgs/invites-sent/${org.id}`, { withCredentials: true });
            setInvitesSent(response.data);
        } catch (error) {
            console.error('Error fetching invites sent:', error);
        } finally {
            setLoadingInvites(false);
        }
    };

    const postMemberRoleChange = async (memberId, newRole, accessLevel, workspaceIds) => {
        try {
            const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/orgs/update-member-role`, {
                orgId: org.id,
                memberId: memberId,
                newRole: newRole,
                accessLevel: accessLevel,
                workspaceIds: workspaceIds || []
            }, { withCredentials: true });
            
            // Refresh members list after successful update
            await fetchMembers();
            return response;
        } catch (error) {
            console.error('Error changing member role', error);
            throw error;
        }
    };

    const postInviteRoleChange = async (email, newRole, accessLevel, workspaceIds) => {
        try {
            const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/orgs/update-invite-role`, {
                orgId: org.id,
                email: email,
                newRole: newRole,
                accessLevel: accessLevel || 'full',
                workspaceIds: workspaceIds || []
            }, { withCredentials: true });
            
            // Refresh invites list after successful update
            await fetchInvitesSent();
            return response;
        } catch (error) {
            console.error('Error changing invite role', error);
            throw error;
        }
    };

    const postRemoveMember = async (member) => {
        const response = await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/orgs/remove-member`, {
            data: {
                orgId: org.id,
                memberId: member.isInvite ? undefined : member.id,
                email: member.email,
                isInvite: member.isInvite,
            },
            withCredentials: true,
        });
        return response;
    };

    /////////////////// HANDLERS ///////////////////
    const handleInviteSuccess = () => {
        // Refresh the invites list after successful invitation
        fetchInvitesSent();
    };

    const handleStatusChange = async (index, newRole) => {
        const member = allMembers[index];
        
        // Check if current user is admin - only admins can change roles
        if (!isCurrentUserAdmin) {
            setErrorMessage('Only admins can change member roles.');
            setUpdateRoleOpen(null);
            return;
        }
        
        if (member.orgRole === newRole) {
            setUpdateRoleOpen(null);
            return;
        }
        
        // Check admin count only for existing members (not invites)
        if (!member.isInvite && member.orgRole === 'admin' && newRole !== 'admin') {
            const adminCount = members.filter(member => member.orgRole === 'admin').length;
            if (adminCount <= 1) {
                setErrorMessage('There must be at least one admin.');
                setUpdateRoleOpen(null);
                return;
            }
        }
        
        try {
            // Determine access level based on new role (common logic for both invites and members)
            let newAccessLevel = 'full';
            let newWorkspaceIds = [];
            
            if (newRole === 'admin') {
                // Admins always have full access
                newAccessLevel = 'full';
                newWorkspaceIds = [];
            } else {
                // For members, preserve their current workspace access if they have limited access
                const currentWorkspaces = member.workspaceIds || [];
                if (member.accessLevel === 'limited' && currentWorkspaces.length > 0) {
                    newAccessLevel = 'limited';
                    newWorkspaceIds = currentWorkspaces;
                } else {
                    newAccessLevel = 'full';
                    newWorkspaceIds = [];
                }
            }
            
            if (member.isInvite) {
                // Handle invite role change
                await postInviteRoleChange(member.email, newRole, newAccessLevel, newWorkspaceIds);
                
                // Update local state
                const updatedInvites = [...invitesSent];
                const inviteIndex = updatedInvites.findIndex(invite => invite.email === member.email);
                if (inviteIndex !== -1) {
                    updatedInvites[inviteIndex].orgRole = newRole;
                    updatedInvites[inviteIndex].accessLevel = newAccessLevel;
                    updatedInvites[inviteIndex].workspaceIds = newWorkspaceIds;
                    setInvitesSent(updatedInvites);
                }
            } else {
                // Handle existing member role change
                await postMemberRoleChange(member.id, newRole, newAccessLevel, newWorkspaceIds);
                
                // Update local state
                const updatedMembers = [...members];
                const memberIndex = updatedMembers.findIndex(m => m.id === member.id);
                if (memberIndex !== -1) {
                    updatedMembers[memberIndex].orgRole = newRole;
                    updatedMembers[memberIndex].accessLevel = newAccessLevel;
                    updatedMembers[memberIndex].workspaceIds = newWorkspaceIds;
                    setMembers(updatedMembers);
                }
            }
        } catch (error) {
            const errorType = member.isInvite ? 'invite' : 'member';
            setErrorMessage(`Failed to update ${errorType} role. Please try again.`);
            console.error(`Error updating ${errorType} role:`, error);
        }
        
        setUpdateRoleOpen(null);
        setErrorMessage('');
    };

    const handleStatusClick = (index) => {
        // Check if current user is admin - only admins can change roles
        if (!isCurrentUserAdmin) {
            setErrorMessage('Only admins can change member roles.');
            return;
        }
        
        setSelectionOpen(selectionOpen === index ? null : index);
        // Close other dropdowns
        setUpdateRoleOpen(null);
        setWorkspaceAccessOpen(null);
    };

    const handleUpdateClick = (index) => {
        if (!isCurrentUserAdmin) {
            setErrorMessage('Only admins can change member roles.');
            return;
        }

        setUpdateRoleOpen(updateRoleOpen === index ? null : index);
        setSelectionOpen(null);
        setWorkspaceAccessOpen(null);
    };

    const handleRemoveClick = (index) => {
        if (!isCurrentUserAdmin) {
            setErrorMessage('Only admins can remove members.');
            return;
        }

        const member = allMembers[index];
        setSelectionOpen(null);
        setUpdateRoleOpen(null);
        setWorkspaceAccessOpen(null);
        setErrorMessage('');
        setRemoveTarget(member);
    };

    const handleConfirmRemove = async () => {
        if (!removeTarget) return;

        const member = removeTarget;

        // Guard against removing the last admin from the client as well.
        if (!member.isInvite && member.orgRole === 'admin') {
            const adminCount = members.filter(m => m.orgRole === 'admin').length;
            if (adminCount <= 1) {
                setErrorMessage('There must be at least one admin.');
                setRemoveTarget(null);
                return;
            }
        }

        setRemovingUser(true);
        try {
            await postRemoveMember(member);

            if (member.isInvite) {
                setInvitesSent(prev => prev.filter(invite => invite.email !== member.email));
            } else {
                setMembers(prev => prev.filter(m => m.id !== member.id));
            }

            setRemoveTarget(null);
        } catch (error) {
            const serverMessage = error?.response?.data?.error;
            setErrorMessage(serverMessage || `Failed to remove ${member.isInvite ? 'invite' : 'member'}. Please try again.`);
            console.error('Error removing member:', error);
            setRemoveTarget(null);
        } finally {
            setRemovingUser(false);
        }
    };

    const handleWorkspaceAccessClick = (index) => {
        // Check if current user is admin - only admins can change workspace access
        if (!isCurrentUserAdmin) {
            setErrorMessage('Only admins can change workspace access.');
            return;
        }

        const newOpenState = workspaceAccessOpen === index ? null : index;
        setWorkspaceAccessOpen(newOpenState);
        
        // Close other dropdowns when opening workspace access
        setSelectionOpen(null);
        setUpdateRoleOpen(null);
    };

    const handleInviteClick = () => {
        // Check if current user is admin - only admins can invite new members
        if (!isCurrentUserAdmin) {
            setErrorMessage('Only admins can invite new members.');
            return;
        }
        
        setInviteModuleOpen(!inviteModuleOpen);
    };

    const handleWorkspaceAccessUpdate = async (errorMessage, newAccessLevel, newWorkspaceIds) => {
        if (errorMessage) {
            setErrorMessage(errorMessage);
            return;
        }

        const member = allMembers[workspaceAccessOpen];
        
        try {
            if (member.isInvite) {
                // Handle invite workspace access update
                await postInviteRoleChange(member.email, member.orgRole, newAccessLevel, newWorkspaceIds);
                
                // Update local state
                const updatedInvites = [...invitesSent];
                const inviteIndex = updatedInvites.findIndex(invite => invite.email === member.email);
                if (inviteIndex !== -1) {
                    updatedInvites[inviteIndex].accessLevel = newAccessLevel;
                    updatedInvites[inviteIndex].workspaceIds = newWorkspaceIds;
                    setInvitesSent(updatedInvites);
                }
            } else {
                // Handle existing member workspace access update
                await postMemberRoleChange(member.id, member.orgRole, newAccessLevel, newWorkspaceIds);
                
                // Update local state
                const updatedMembers = [...members];
                const memberIndex = updatedMembers.findIndex(m => m.id === member.id);
                if (memberIndex !== -1) {
                    updatedMembers[memberIndex].accessLevel = newAccessLevel;
                    updatedMembers[memberIndex].workspaceIds = newWorkspaceIds;
                    setMembers(updatedMembers);
                }
            }
            
            setWorkspaceAccessOpen(null);
        } catch (error) {
            const errorType = member.isInvite ? 'invite' : 'member';
            setErrorMessage(`Failed to update ${errorType} workspace access. Please try again.`);
            console.error(`Error updating ${errorType} workspace access:`, error);
        }
    };

    /////////////////// RENDERERS ///////////////////
    const renderStatusSelection = () => {
        const member = allMembers[selectionOpen];
        const isSelf = member && member.email === user.email;

        return (
            <div className="selection-menu">
                <div
                    className="status-option"
                    data-role="admin"
                    onClick={() => handleStatusChange(selectionOpen, 'admin')}
                >
                    Admin
                </div>
                <div
                    className="status-option"
                    data-role="member"
                    onClick={() => handleStatusChange(selectionOpen, 'member')}
                >
                    Member
                </div>
                {!isSelf && (
                    <>
                        <div className="selection-divider" />
                        <div
                            className="status-option revoke"
                            onClick={() => handleRemoveClick(selectionOpen)}
                        >
                            {member?.isInvite ? 'Revoke Invite' : 'Remove User'}
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderRemoveConfirmation = () => {
        if (!removeTarget) return null;

        const isInvite = removeTarget.isInvite;
        const label = removeTarget.name || removeTarget.email;

        return (
            <Popup
                onClose={() => !removingUser && setRemoveTarget(null)}
                content={
                    <div className="remove-member-popup">
                        <h3>{isInvite ? 'Revoke invitation' : 'Remove user'}</h3>
                        <p>
                            {isInvite
                                ? <>Are you sure you want to revoke the invitation for <strong>{removeTarget.email}</strong>? They will no longer be able to join this organization with this invite.</>
                                : <>Are you sure you want to remove <strong>{label}</strong> from this organization? They will immediately lose access to all of its workspaces.</>}
                        </p>
                        <div className="remove-member-actions">
                            <button
                                className="cancel-button"
                                onClick={() => setRemoveTarget(null)}
                                disabled={removingUser}
                            >
                                Cancel
                            </button>
                            <button
                                className="confirm-remove-button"
                                onClick={handleConfirmRemove}
                                disabled={removingUser}
                            >
                                {removingUser ? 'Removing...' : (isInvite ? 'Revoke Invite' : 'Remove User')}
                            </button>
                        </div>
                    </div>
                }
            />
        );
    };



    return (
        <div className="settings-component">
            <div className="settings-header">
                <div className="settings-title-group">
                    <h1 className="settings-title">Organization Members</h1>
                    <h2 className="settings-subtitle">Manage your organization's members and invite new ones</h2>
                </div>
            </div>
            
            {(loadingMembers || loadingInvites) && (
                <LoadingScreen message="Loading organization members..." />
            )}
            
            <div className="content members-settings">
                <InviteModule 
                    isOpen={inviteModuleOpen}
                    onClose={() => setInviteModuleOpen(false)}
                    org={org}
                    user={user}
                    onInviteSuccess={handleInviteSuccess}
                    setupSteps={setupSteps}
                    setSetupSteps={setSetupSteps}
                />

                <div className="members-section">
                    <div className="members-section-header">
                        <h3>Team Members</h3>
                        <button
                            className={`invite-button${!isCurrentUserAdmin ? ' disabled' : ''}`}
                            onClick={handleInviteClick}
                            disabled={!isCurrentUserAdmin}
                        >
                            Invite
                        </button>
                    </div>
                    <div className="members-table-container">
                        <table className="members-table">
                            <thead>
                                <tr>
                                    <th>Member</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Workspace Access</th>
                                    {/* <th>Actions</th> */}
                                </tr>
                            </thead>
                            <tbody>
                                {(loadingMembers || loadingInvites) ? (
                                    <tr>
                                        <td colSpan="4" className="loading-state">Loading members...</td>
                                    </tr>
                                ) : allMembers && allMembers.length > 0 ? allMembers.map((member, index) => (
                                    <tr key={index} className="member-row">
                                        <td className="member-cell">
                                            <div className="member-details">
                                                <CircleInitials text={member.name} />
                                                <div className="member-info">
                                                    <div>
                                                        {member.name}
                                                        {member.email === user.email && (
                                                            <span className="you-tag">You</span>
                                                        )}
                                                    </div>
                                                    <div>{member.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="role-cell">
                                            <div className={`member-status ${member.orgRole}`}>
                                                <div
                                                    className={`status${!isCurrentUserAdmin ? ' disabled' : ''}`}
                                                    onClick={() => handleStatusClick(index)}
                                                >
                                                    <p>{member.orgRole.charAt(0).toUpperCase() + member.orgRole.slice(1)}</p>
                                                    {isCurrentUserAdmin && <DownIcon className="down-icon" />}
                                                </div>
                                                {selectionOpen === index && renderStatusSelection()}
                                            </div>
                                        </td>
                                        <td className="status-cell">
                                            <span className={`status-type ${member.isInvite ? 'pending' : 'accepted'}`}>
                                                {member.isInvite ? 'Pending' : 'Accepted'}
                                            </span>
                                        </td>
                                        <td className="workspaces-cell">
                                            <div className={`workspace-access ${member.orgRole}`}>
                                                {member.orgRole === 'admin' ? (
                                                    <div className="workspace-access-status">
                                                        <span className="workspace-access-text">All (Admin)</span>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div
                                                            className={`workspace-access-status${!isCurrentUserAdmin ? ' disabled' : ''}`}
                                                            onClick={() => handleWorkspaceAccessClick(index)}
                                                        >
                                                            <span className="workspace-access-text">
                                                                {member.accessLevel === 'full' ? 'All' : 'Limited'}
                                                            </span>
                                                            {isCurrentUserAdmin && <DownIcon className="down-icon" />}
                                                        </div>
                                                        {workspaceAccessOpen === index && (
                                                            <WorkspaceAccessDropdown
                                                                member={member}
                                                                isOpen={workspaceAccessOpen === index}
                                                                onClose={() => setWorkspaceAccessOpen(null)}
                                                                onUpdate={handleWorkspaceAccessUpdate}
                                                                user={user}
                                                                org={org}
                                                                isCurrentUserAdmin={isCurrentUserAdmin}
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                        {/* <td className="actions-cell">
                                            {member.isInvite ? (
                                                <button 
                                                    className="action-button revoke-button"
                                                    onClick={() => handleRevokeInvite(member.id, member.email)}
                                                >
                                                    Revoke Invite
                                                </button>
                                            ) : (
                                                <div className="actions-container">
                                                    <button 
                                                        className="action-button remove-button"
                                                        onClick={() => handleRemoveMember(member.id, member.name)}
                                                    >
                                                        Remove
                                                    </button>
                                                    {updateRoleOpen === index && renderRoleSelection(index)}
                                                </div>
                                            )}
                                        </td> */}
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="4" className="empty-state">No members found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {errorMessage && <div className="error-message">{errorMessage}</div>}
            </div>

            {renderRemoveConfirmation()}
        </div>
    );
}

export default OrgMembersSettings;